import React, {useEffect, useState} from "react";
import Title from "../../../components/Title";
import {
  useGetConsentRequestLayoutPreviewQuery,
  useGetConsentRequestLayoutsQuery,
  useUpdateConsentRequestLayoutMutation
} from "../../../api/consentRequestLayoutApiSlice";
import {Block, FormGroup, Loader, useForm, Yup} from "../../../components/components";
import Table from "../../../components/tables/Table";
import {useTranslation} from "react-i18next";
import useCustomTable from "../../../components/tables/useCustomTable";
import Icon from "../../../components/Icon";
import {Button, Col, Form, Modal, Row, Tab} from "react-bootstrap";
import VendorForm from "../../../components/cmp/VendorForm";
import BoolSelect from "../../../components/forms/inputs/BoolSelect";
import ShowItem from "../../../components/ShowItem";
import useActionConfirmation from "../../../components/useActionConfirmation";
import {CmpPreview} from "../../../components/cmp/CmpPreview";
import {useGetCmpPreviewQuery, useGetDecodeConsentStringQuery} from "../../../api/cmpApiSlice";
import Nav from "react-bootstrap/Nav";
import Select from "../../../components/forms/inputs/Select";
import {useGetAppsQuery} from "../../../api/appApiSlice";
import {useFormattedLanguage} from "../../../components/utils";


export default function ConsentRequestLayouts(props) {

  const { t } = useTranslation()
  const [showModal, setShowModal] = useState(false)
  const [layout, setLayout] = useState()

  const {data: layouts = [], isSuccess, isFetching: isLoading} = useGetConsentRequestLayoutsQuery()
  const [updateLayout] = useUpdateConsentRequestLayoutMutation()

  const loaderProps = { success: (!isLoading && isSuccess), error: (!isLoading && !isSuccess) }

  const columns = [{
    Header: 'Version',
    accessor: 'version'
  }, {
    Header: 'Status',
    accessor: 'status',
    Cell: ({row}) => (<StatusBadge status={row.original.status}/>)
  }, {
    Header: 'Created at',
    accessor: 'createdAt',
    format: 'date'
  }, {
    Header: 'Updated at',
    accessor: 'updatedAt',
    format: 'date'
  }, {
    Header: 'Commit ID',
    accessor: 'commitId'
  }, {
    Header: 'TCF version',
    accessor: 'tcfVersion'
  }];

  const tableOptions = {
    sortable: true,
    paginate: false,
    filterable: false,
    sortBy: {
      [columns[0].accessor]: 'desc'
    }
  };

  const {
    tableProps
  } = useCustomTable(tableOptions)

  const showLayout = (layout) => {
    setLayout(layout)
    setShowModal(true)
  }

  const handleClose = () => {
    setShowModal(false)
    setLayout(null)
  }

  return (
    <>
      <Title main={t('cmp.consentRequestLayouts')} />
      <Block>
        <Loader {...loaderProps} >
          <Table
            data={layouts}
            columns={columns}
            onRowClick={showLayout}
            {...tableProps} />
        </Loader>
      </Block>
      <ConsentRequestLayoutModal show={showModal} handleClose={handleClose} layout={layout} updateLayout={updateLayout}/>
    </>
  )
}

const StatusBadge = ({status}) => {
  let icon, color;

  if(status == 'approved') {
    [icon, color] = ['play', 'green']
  } else if(status == 'approved') {
    [icon, color] = ['hourglass', 'grey']
  } else if(status == 'pending') {
    [icon, color] = ['times', 'red']
  } else if(status == 'testing') {
    [icon, color] = ['vial', '#F9A602']
  }

  return <Icon icon={icon} style={{color: color}} />
}

const ConsentRequestLayoutModal = ({show, handleClose, layout, updateLayout}) => {
  const { t, i18n } = useTranslation()
  const [editable, setEditable] = useState(false)
  const [submitButtonProps, setSubmitButtonProps] = useState({})
  const [appPreviewId, setAppPreviewId] = useState()
  const [consentString, setConsentString] = useState()
  const [lang, setLang] = useState(i18n.language)
  const [showPreview, setShowPreview] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const {Confirmation, confirmationProps, confirm} = useActionConfirmation()
  const {data: preview = {}, isSuccess: isAppPreviewSuccess, isFetching: isAppPreviewLoading} = useGetConsentRequestLayoutPreviewQuery({id: layout?.id, appId: appPreviewId, lang: lang}, {skip: !(layout?.id && showPreview)})
  const {data: decodedConsentString = {}, isConsentStringSuccess} = useGetDecodeConsentStringQuery({consentString}, {skip: !consentString})
  const {data: apps, isLoading} = useGetAppsQuery()

  if (!layout) {
    return <></>
  }

  const onSubmit = (data) => {
    updateLayout({id: layout.id, layout: data}).unwrap().then(() => {
      setEditable(false)
      handleClose()
    })
  }

  const handleTest = (data) => {
    confirm(() => updateLayout({id: layout.id, layout: {status: "testing"}}).unwrap().then(() => {
      handleClose()
    }))
  }

  const handleReject = (data) => {
    confirm(() => updateLayout({id: layout.id, layout: {status: "rejected"}}).unwrap().then(() => {
      handleClose()
    }))
  }

  const handleApprove = (data) => {
    confirm(() => updateLayout({id: layout.id, layout: {status: "approved"}}).unwrap().then(() => {
      handleClose()
    }))
  }

  const handlePreview = (data) => {
    setShowPreview(true)
  }

  const cmpPreviewProps = {
    show: showPreview,
    handleClose: () => setShowPreview(false),
    preview: preview,
    setConsentString: setConsentString,
    decodedConsentString: decodedConsentString,
    isAppPreviewLoading: isAppPreviewLoading,
    isAppPreviewSuccess: isAppPreviewSuccess
  }

  return (
    <>
      <Modal show={show} onHide={handleClose} size={"lg"}>
        <Modal.Header closeButton>
          <Modal.Title>{layout.commitId}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!editable && <ConsentRequestLayoutShow layout={layout}/>}
          {editable && <ConsentRequestLayoutForm layout={layout} setSubmitButtonProps={setSubmitButtonProps} onSubmit={onSubmit}/>}
        </Modal.Body>
        <Modal.Footer>
          {!editable && <>
            <Button variant="secondary" onClick={handleClose}>
              {t("cancel")}
            </Button>
            <Button onClick={() => setEditable(true)}>
              <Icon icon='edit' text={t("edit")}/>
            </Button>
            <Button onClick={handlePreview} variant="secondary">
              Preview
            </Button>
            <Button onClick={handleTest} variant="warning">
              Test
            </Button>
            <Button onClick={handleReject} variant="danger">
              Reject
            </Button>
            <Button onClick={handleApprove} variant="success">
              Approve
            </Button>
          </>}
          {editable && <>
            <Button variant="secondary" onClick={() => setEditable(false)}>
              {t("cancel")}
            </Button>
            <Button key="submit" type="submit" variant="primary" {...submitButtonProps}>
              <Icon icon='save' text={t("save")}/>
            </Button>
          </>}
        </Modal.Footer>
      </Modal>
      <Confirmation {...confirmationProps} />
      <CmpPreview {...cmpPreviewProps} showSettings={showSettings}>
        <CmpPreviewExtraOptions
          setAppPreviewId={setAppPreviewId}
          apps={apps}
          setLang={setLang}
          lang={lang}
          setShowSettings={setShowSettings}
          showSettings={showSettings}/>
      </CmpPreview>
    </>
  )
}

const ConsentRequestLayoutShow = ({layout}) => {
  const { t } = useTranslation()

  return (
    <>
      <ShowItem label={"Status"} value={layout.status} darkBorder/>
      <ShowItem label={"Version"} value={layout.version} darkBorder/>
      <ShowItem label={"TCF Version"} value={layout.tcfVersion} darkBorder/>
      <ShowItem label={"Description"} value={layout.description} darkBorder/>
    </>
  )
}

const ConsentRequestLayoutForm = ({layout, setSubmitButtonProps, onSubmit}) => {
  const initialValues = {
    version: '',
    description: ''
  };

  const validationSchema = Yup.object().shape({
    version: Yup.number().required(),
    description: Yup.string()
  })

  const { getInputProps, formProps, formData } = useForm({
    defaultObject: layout,
    initialValues: initialValues,
    validationSchema: validationSchema,
    setSubmitButtonProps: setSubmitButtonProps,
    onSubmit: onSubmit,
  });


  return <Form {...formProps}>
    <FormGroup input="text" label={"Version"} {...getInputProps("version")}/>
    <FormGroup input="textarea" rows={3} label={"Description"} {...getInputProps("description")}/>
  </Form>
}

const CmpPreviewExtraOptions = ({setAppPreviewId, apps, setLang, lang, setShowSettings, showSettings}) => {
  const { t } = useTranslation()
  const formatLanguage = useFormattedLanguage(t)
  const [appId, setAppId] = useState()
  const [options, setOptions] = useState()

  useEffect(() => {
    setAppPreviewId(appId)
  }, [appId])

  useEffect(() => {
    setOptions(apps.map(app => ({value: app.id, label: app.name})))
  }, [apps])

  const marginStyle = {
    marginBottom: "10px"
  }

  const toggleShowSettings = () => {
    setShowSettings(!showSettings)
  }

  return (
    <>
      <div className="title">
        Preview options
      </div>
      <div className="preview-output-content">
        <Select placeholder={"Select an app"} options={options} value={appId} onChange={setAppId} style={marginStyle}/>
        <Select placeholder={"Select a language"} options={["fr", "en"].map(lang => ({value: lang, label: formatLanguage(lang)}))} value={lang} onChange={setLang} style={marginStyle}/>
        <Button onClick={toggleShowSettings} variant={"primary"} style={marginStyle}>{showSettings ? "Hide" : "Show"} settings</Button>
      </div>
    </>
  )
}
